import { useEffect, useRef, useState } from "react";
import { X, Send, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * OrderChatDrawer
 * A right-side drawer that shows the customer <-> driver chat for a given order.
 * Polls /api/orders/{id}/messages every 3s while open. Enter (or Send button)
 * fires POST /messages. Messages are grouped by sender bubble.
 *
 * Props:
 *  - orderId: string
 *  - meRole: "customer" | "driver"
 *  - counterpartName: string (shown in the header)
 *  - onClose: () => void
 *  - dark?: boolean — dark theme (driver view)
 *  - disabled?: boolean — if true, input+send are locked (e.g. before on_the_way)
 *  - disabledReason?: string — helper text shown when disabled
 */
export default function OrderChatDrawer({
  orderId,
  meRole,
  counterpartName = "Driver",
  onClose,
  dark = false,
  disabled = false,
  disabledReason = "",
}) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/messages`);
      setMessages(data.messages || []);
    } catch (e) {
      // 403 while state is transitional — silent
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [orderId]);

  useEffect(() => {
    // Auto-scroll to the newest message
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async () => {
    const clean = text.trim();
    if (!clean || sending || disabled) return;
    setSending(true);
    // Optimistic append
    const optimistic = {
      id: `tmp-${Date.now()}`,
      sender: meRole,
      text: clean,
      at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((m) => [...m, optimistic]);
    setText("");
    try {
      const { data } = await api.post(`/orders/${orderId}/messages`, { text: clean });
      setMessages((m) => m.map((x) => (x.id === optimistic.id ? data.message : x)));
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const surfaceCls = dark
    ? "bg-eatup-dark-surface text-white border-l border-white/10"
    : "bg-white text-eatup-charcoal border-l border-neutral-200";
  const subtleTxt = dark ? "text-neutral-400" : "text-neutral-500";

  const quickReplies = meRole === "customer"
    ? ["I'm at the side gate", "Gate code is 1234", "Please leave at the door", "I'll be right out"]
    : ["I'm 2 min away", "I've arrived", "What's the gate code?", "Which door should I use?"];

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end fade-up"
      data-testid="order-chat-drawer"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full sm:w-[420px] h-full ${surfaceCls} shadow-2xl flex flex-col animate-[slideInR_0.25s_ease-out]`} style={{ animation: "slideInR 0.25s ease-out" }}>
        {/* Header */}
        <div className={`p-4 flex items-center gap-3 ${dark ? "border-b border-white/10" : "border-b border-neutral-100"}`}>
          <div className={`w-10 h-10 rounded-full ${dark ? "bg-eatup-green/20 text-eatup-green" : "bg-eatup-green/15 text-eatup-green"} flex items-center justify-center`}>
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] uppercase tracking-widest font-black ${dark ? "text-eatup-yellow" : "text-eatup-green"}`}>
              Chat with {meRole === "customer" ? "driver" : "customer"}
            </div>
            <div className="font-display font-bold truncate">{counterpartName}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="chat-close-btn"
            className={`w-9 h-9 rounded-full ${dark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-neutral-100 hover:bg-neutral-200 text-eatup-charcoal"} flex items-center justify-center transition`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className={`text-center text-sm ${subtleTxt} mt-6`}>
              No messages yet. Send the first line to coordinate the drop.
            </div>
          )}
          {messages.map((m) => {
            const mine = m.sender === meRole;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                <div
                  className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm ${
                    mine
                      ? "bg-eatup-green text-white rounded-br-md"
                      : dark
                        ? "bg-white/10 text-white rounded-bl-md"
                        : "bg-neutral-100 text-eatup-charcoal rounded-bl-md"
                  } ${m._pending ? "opacity-70" : ""}`}
                >
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`mt-1 text-[10px] ${mine ? "text-white/70" : subtleTxt}`}>
                    {new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick replies */}
        {!disabled && (
          <div className={`px-4 pt-2 pb-1 flex gap-2 overflow-x-auto no-scrollbar`}>
            {quickReplies.map((q) => (
              <button
                key={q}
                onClick={() => setText(q)}
                data-testid={`chat-quick-${q.slice(0, 10).replace(/\s/g, "-").toLowerCase()}`}
                className={`shrink-0 text-xs font-semibold rounded-full px-3 py-1.5 border transition ${
                  dark
                    ? "bg-white/5 border-white/10 hover:border-eatup-green/50 text-white"
                    : "bg-neutral-50 border-neutral-200 hover:border-eatup-green/50 text-eatup-charcoal"
                }`}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Composer */}
        <div className={`p-3 ${dark ? "border-t border-white/10" : "border-t border-neutral-100"}`}>
          {disabled && (
            <div className={`text-[11px] font-semibold mb-2 text-center ${subtleTxt}`}>
              {disabledReason || "Chat opens when the driver is on the way."}
            </div>
          )}
          <div className={`flex items-end gap-2 rounded-2xl p-2 ${dark ? "bg-white/5 border border-white/10" : "bg-neutral-50 border border-neutral-200"}`}>
            <textarea
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              disabled={disabled}
              placeholder={disabled ? "Chat locked" : "Type a message…"}
              className={`flex-1 resize-none outline-none bg-transparent px-2 py-1.5 text-sm placeholder:${subtleTxt} max-h-24 disabled:opacity-50`}
            />
            <button
              onClick={send}
              disabled={sending || disabled || !text.trim()}
              data-testid="chat-send-btn"
              className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
                sending || disabled || !text.trim()
                  ? "bg-neutral-300 text-neutral-500 cursor-not-allowed"
                  : "bg-eatup-green text-white hover:bg-eatup-green-dark active:scale-95"
              }`}
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideInR {
          from { transform: translateX(20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
